import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import { api, setCsrf, ApiError } from './api';

export { browserSupportsWebAuthn };

// Derive the option JSON shapes from the library's own signatures, so we don't depend
// on specific exported type names across versions.
type CreationOptionsJSON = Parameters<typeof startRegistration>[0]['optionsJSON'];
type RequestOptionsJSON = Parameters<typeof startAuthentication>[0]['optionsJSON'];

interface Device {
  id: string;
  label: string;
}

/** First passkey: gated by the one-time bootstrap secret. */
export async function registerFirstPasskey(label: string, bootstrapSecret: string): Promise<Device> {
  const { options } = await api.post<{ options: CreationOptionsJSON }>(
    '/auth/register/options',
    { bootstrap_secret: bootstrapSecret },
  );
  const credential = await startRegistration({ optionsJSON: options });
  const res = await api.post<{ device: Device }>('/auth/register/verify', { credential, label });
  return res.device;
}

/** Add a passkey/backup key for an already-authenticated operator (requires step-up).
 *  Call via withStepUp('register_device', (t) => addPasskey(label, t)). */
export async function addPasskey(label: string, stepUpToken?: string): Promise<Device> {
  const { options } = await api.post<{ options: CreationOptionsJSON }>(
    '/auth/register/options',
    {},
    stepUpToken,
  );
  const credential = await startRegistration({ optionsJSON: options });
  const res = await api.post<{ device: Device }>('/auth/register/verify', { credential, label }, stepUpToken);
  return res.device;
}

/** Operatore loggato: conia un codice monouso (+ step-up) per registrare un NUOVO dispositivo.
 *  Call via withStepUp('register_device', (t) => createEnrollCode(label, t)). */
export async function createEnrollCode(label: string, stepUpToken?: string): Promise<{ code: string; expires_in_min: number }> {
  return api.post<{ code: string; expires_in_min: number }>(
    '/auth/enroll/create',
    { label },
    stepUpToken,
  );
}

/** Dispositivo NUOVO (non loggato): registra una passkey usando un codice monouso.
 *  Nessuna sessione né step-up: il codice valido è la prova. */
export async function registerWithEnrollCode(code: string, label: string): Promise<Device> {
  const { options } = await api.post<{ options: CreationOptionsJSON }>(
    '/auth/register/options',
    { enroll_code: code },
  );
  const credential = await startRegistration({ optionsJSON: options });
  const res = await api.post<{ device: Device }>('/auth/register/verify', { credential, label, enroll_code: code });
  return res.device;
}

export async function loginWithPasskey(): Promise<Device> {
  const { options } = await api.post<{ options: RequestOptionsJSON }>(
    '/auth/login/options',
    {},
  );
  const assertion = await startAuthentication({ optionsJSON: options });
  const res = await api.post<{ csrf_token: string; device: Device }>('/auth/login/verify', { assertion });
  setCsrf(res.csrf_token);
  return res.device;
}

/** Fresh, action-scoped step-up assertion → short-lived token for the X-StepUp header. */
export async function stepUp(action: string): Promise<string> {
  const { options } = await api.post<{ options: RequestOptionsJSON }>(
    '/auth/stepup/challenge',
    { action },
  );
  const assertion = await startAuthentication({ optionsJSON: options });
  const res = await api.post<{ step_up_token: string }>('/auth/stepup/verify', { assertion, action });
  return res.step_up_token;
}

/** Run an action; if the server demands step-up, perform the ceremony and retry once. */
export async function withStepUp<T>(action: string, run: (token?: string) => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof ApiError && e.code === 'step_up_required') {
      const act = (e.details as { action?: string } | undefined)?.action ?? action;
      const token = await stepUp(act);
      return run(token);
    }
    throw e;
  }
}
